import React, { useEffect, useState, useContext } from 'react';
import { useNavigate } from 'react-router';
import { FaSearch } from 'react-icons/fa';
import axios from 'axios';
import { toast } from 'react-toastify';
import ls from 'localstorage-slim';
import { useTranslation } from 'react-i18next';

import DataContext from '../context/InvoiceData';
import { refreshToken, getETAToken, updateUserLastLogin } from '../helpers';

function IssuerReceiver() {
  const {
    step,
    setStep,
    documentType,
    documentTypeVersion,
    setDocumentType,
    setDocumentTypeVersion,
    setInvoiceSubType,
  } = useContext(DataContext);

  let navigate = useNavigate();
  const { t, i18n } = useTranslation();

  const [issuerData, setIssuerData] = useState({});
  const [receiverData, setReceiverData] = useState('');
  const [receiverRin, setReceiverRin] = useState('');
  const [activityCodes, setActivityCodes] = useState([]);
  const [activityCode, setActivityCode] = useState('');

  let invoiceId = localStorage.getItem('id');
  let taxpayerId = localStorage.getItem('taxpayer_id');
  const subDomain = ls.get('subDomain');

  let handleSearchReceiver = () => {
    axios
      .get(`${subDomain}${process.env.REACT_APP_DJANGO_BASEURL}/receivers/${receiverRin}`)
      .then(response => {
        setReceiverData(response.data);
      })
      .catch(error => {
        console.log(error.message);
      });
  };

  let handleReceiverRin = event => {
    setReceiverRin(event.target.value);
  };

  let handleActivityCode = event => {
    setActivityCode(parseInt(event.target.value));
  };

  const onChange = nextStep => {
    setStep(nextStep < 0 ? 0 : nextStep > 5 ? 5 : nextStep);
  };

  const handleClose = () => {
    setStep(0);
    setDocumentTypeVersion('');
    setDocumentType('');
    setInvoiceSubType('');
    ls.remove('id');
    navigate('../../document/new');
  };

  const handleContinue = () => {
    const updateToast = toast.loading('Please wait...');
    axios
      .put(`${subDomain}${process.env.REACT_APP_DJANGO_BASEURL}/invoices/${invoiceId}`, {
        issuer: issuerData.id,
        receiver: receiverData.id,
        activity_code: activityCode,
        status: 'draft',
      })
      .then(response => {
        toast.update(updateToast, {
          render: `${response.data['message']}`,
          type: 'success',
          isLoading: false,
          autoClose: 1000,
          theme: 'colored',
        });
        onChange(step + 1);
      })
      .catch(error => {
        console.log(error);
      });
  };

  const handlePrevious = () => {
    onChange(step - 1);
  };

  useEffect(() => {
    async function fetchData() {
      try {
        if (!ls.get('access')) {
          await refreshToken();
          await updateUserLastLogin();
        }
        if (!ls.get('ETAToken')) await getETAToken();

        let response = await axios.get(
          `${subDomain}${process.env.REACT_APP_DJANGO_BASEURL}/erp/current_taxpayer_activity_codes`,
          {
            params: {
              taxpayer: ls.get('taxpayer_id'),
            },
          }
        );

        setActivityCodes(response.data);
      } catch (error) {
        if (error.response) {
          // Request made but the server responded with an error
          console.log(error.response.data);
        } else if (error.request) {
          // Request made but no response is received from the server.
          console.log(error.request);
        } else {
          // Error occured while setting up the request
          console.log('Error', error.message);
        }
      }
    }

    fetchData();
  }, []);

  useEffect(() => {
    async function fetchData() {
      try {
        let response = await axios.get(
          `${subDomain}${process.env.REACT_APP_DJANGO_BASEURL}/issuers/${taxpayerId}`
        );

        setIssuerData(response.data);
      } catch (error) {
        if (error.response) {
          // Request made but the server responded with an error
          console.log(error.response.data);
        } else if (error.request) {
          // Request made but no response is received from the server.
          console.log(error.request);
        } else {
          // Error occured while setting up the request
          console.log('Error', error.message);
        }
      }
    }

    fetchData();
  }, []);

  return (
    <>
      <div className={`${i18n.language === 'ar' && 'text-end'} card border-0 shadow m-3`}>
        <div className='card-body body m-5'>
          <span className='p-2 fw-bold fs-5'>{`${documentType} | ${documentTypeVersion}`}</span>
          <div
            className={`${
              i18n.language === 'en' ? 'row mx-0' : 'd-flex flex-row-reverse'
            } my-3 p-2`}>
            <span
              className='subtitle text-decoration-underline pb-2'
              style={{ textUnderlineOffset: '10px' }}>
              <strong className='fs-5'>{t('stepper.issuerReceiver.title')}</strong>
            </span>
          </div>
          <div className='row'>
            <div className={`${i18n.language === 'en' ? 'order-1' : 'order-2'} col`}>
              <div className='card mx-auto'>
                <div className='card-body'>
                  <h5>{t('stepper.issuerReceiver.issuer')}</h5>
                  <div>
                    <div className='input-group mt-2 mb-2'>
                      <input
                        id='rin'
                        name='rin'
                        type='text'
                        dir={`${i18n.language === 'ar' && 'rtl'}`}
                        className='form-control me-2'
                        placeholder='Issuer Registeration Number'
                        defaultValue={issuerData ? issuerData.rin : ''}
                        disabled
                      />
                      <select
                        id='taxpayerActivityCode'
                        className='form-select'
                        aria-label='Default select example'
                        name='taxpayerActivityCode'
                        dir={`${i18n.language === 'ar' && 'rtl'}`}
                        onChange={handleActivityCode}>
                        <option value=''>{t('stepper.issuerReceiver.activityCode')}</option>
                        {activityCodes.map(activityCode => (
                          <option key={activityCode.id} value={activityCode.id}>
                            {activityCode.code}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className='input-group mb-2'>
                      <input
                        id='type'
                        className='form-control'
                        aria-label='Default select example'
                        name='type'
                        placeholder='Type'
                        dir={`${i18n.language === 'ar' && 'rtl'}`}
                        value={
                          issuerData.type === 'B'
                            ? 'Business'
                            : issuerData.type === 'F'
                            ? 'Foreign'
                            : issuerData.type === 'P'
                            ? 'Personal'
                            : ''
                        }
                        disabled
                      />
                    </div>

                    <div className='input-group mb-2'>
                      <input
                        id='company_name'
                        name='company_name'
                        type='text'
                        className='form-control me-2'
                        placeholder='Issuer Company Name'
                        defaultValue={issuerData ? issuerData.company_name_ar : ''}
                        dir={`${i18n.language === 'ar' && 'rtl'}`}
                        disabled
                      />
                      <input
                        id='branch_id'
                        name='branch_id'
                        type='text'
                        className='form-control'
                        placeholder='Branch ID'
                        defaultValue={issuerData ? issuerData.branch_id : ''}
                        dir={`${i18n.language === 'ar' && 'rtl'}`}
                        disabled
                      />
                    </div>
                    <div className='input-group mb-2'>
                      <input
                        id='governorate'
                        name='governorate'
                        type='text'
                        className='form-control me-2'
                        placeholder='Governorate'
                        defaultValue={issuerData ? issuerData.governorate : ''}
                        dir={`${i18n.language === 'ar' && 'rtl'}`}
                        disabled
                      />
                      <input
                        id='region_city'
                        name='region_city'
                        type='text'
                        className='form-control'
                        placeholder='Region City'
                        defaultValue={issuerData ? issuerData.region_city : ''}
                        dir={`${i18n.language === 'ar' && 'rtl'}`}
                        disabled
                      />
                    </div>

                    <div className='input-group'>
                      <input
                        id='street'
                        name='street'
                        type='text'
                        className='form-control me-2'
                        placeholder='Street'
                        defaultValue={issuerData ? issuerData.street : ''}
                        dir={`${i18n.language === 'ar' && 'rtl'}`}
                        disabled
                      />
                      <input
                        id='building_number'
                        name='building_number'
                        type='text'
                        className='form-control'
                        placeholder='Building Number'
                        defaultValue={issuerData ? issuerData.building_number : ''}
                        dir={`${i18n.language === 'ar' && 'rtl'}`}
                        disabled
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className={`${i18n.language === 'en' ? 'order-2' : 'order-1'} col`}>
              <div className='card mx-auto'>
                <div className='card-body'>
                  <h5>{t('stepper.issuerReceiver.receiver')}</h5>
                  <div>
                    <div className='input-group mt-2 mb-2'>
                      <input
                        id='rin'
                        name='rin'
                        type='text'
                        pattern='^\d{9}+$'
                        title='Receiver RIN should only contain numbers. e.g. 560570580'
                        className='form-control'
                        placeholder={t('stepper.issuerReceiver.rin')}
                        dir={`${i18n.language === 'ar' && 'rtl'}`}
                        onChange={handleReceiverRin}
                        required
                      />
                      <span
                        className='input-group-text'
                        id='basic-addon1'
                        role='button'
                        onClick={handleSearchReceiver}>
                        {<FaSearch />}
                      </span>
                    </div>
                    <div className='input-group mb-2'>
                      <input
                        id='type'
                        className='form-control'
                        aria-label='Default select example'
                        name='type'
                        placeholder={t('stepper.issuerReceiver.type')}
                        value={
                          receiverData.type === 'B'
                            ? 'Business'
                            : receiverData.type === 'F'
                            ? 'Foreign'
                            : receiverData.type === 'P'
                            ? 'Personal'
                            : ''
                        }
                        dir={`${i18n.language === 'ar' && 'rtl'}`}
                        disabled
                      />
                    </div>
                    <div className='input-group mb-2'>
                      <input
                        id='company_name'
                        name='company_name'
                        type='text'
                        className='form-control'
                        placeholder={t('stepper.issuerReceiver.company')}
                        defaultValue={receiverData ? receiverData.company_name_ar : ''}
                        dir={`${i18n.language === 'ar' && 'rtl'}`}
                        disabled
                      />
                    </div>
                    <div className='input-group mb-2'>
                      <input
                        id='governorate'
                        name='governorate'
                        type='text'
                        className='form-control me-2'
                        placeholder={t('stepper.issuerReceiver.governorate')}
                        defaultValue={receiverData ? receiverData.governorate : ''}
                        dir={`${i18n.language === 'ar' && 'rtl'}`}
                        disabled
                      />
                      <input
                        id='region_city'
                        name='region_city'
                        type='text'
                        className='form-control'
                        placeholder={t('stepper.issuerReceiver.region')}
                        defaultValue={receiverData ? receiverData.region_city : ''}
                        dir={`${i18n.language === 'ar' && 'rtl'}`}
                        disabled
                      />
                    </div>
                    <div className='input-group'>
                      <input
                        id='street'
                        name='street'
                        type='text'
                        className='form-control me-2'
                        placeholder={t('stepper.issuerReceiver.street')}
                        defaultValue={receiverData ? receiverData.street : ''}
                        dir={`${i18n.language === 'ar' && 'rtl'}`}
                        disabled
                      />
                      <input
                        id='building_number'
                        name='building_number'
                        type='text'
                        className='form-control'
                        placeholder={t('stepper.issuerReceiver.building')}
                        defaultValue={receiverData ? receiverData.building_number : ''}
                        dir={`${i18n.language === 'ar' && 'rtl'}`}
                        disabled
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div
        className={`${
          i18n.language === 'en' ? 'row' : 'd-flex flex-row-reverse'
        } justify-content-evenly fixed-bottom py-3 bg-secondary bg-gradient bg-opacity-10`}>
        <button
          className={`${
            i18n.language === 'ar' && 'offset-6'
          } button text-center col-1 bg-white text-dark border border-1 border-dark`}
          onClick={handleClose}>
          {t('stepper.footer.close')}
        </button>
        <button
          className={`${i18n.language === 'en' && 'offset-6'} button text-center col-1 ${
            step === 0 ? 'bg-secondary bg-gradient' : ''
          }`}
          onClick={handlePrevious}
          disabled={step === 0}>
          {t('stepper.footer.previous')}
        </button>
        <button
          className={`button text-center col-1 ${
            !activityCode || !receiverData ? 'bg-secondary bg-gradient' : ''
          }`}
          onClick={handleContinue}
          disabled={!activityCode || !receiverData}>
          {t('stepper.footer.continue')}
        </button>
      </div>
    </>
  );
}

export default IssuerReceiver;
